"use client";

import Link from "next/link";
import Image from "next/image";
import { STATIC_ASSETS } from "@/lib/cdn";
import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useT } from "@/hooks/useT";

// Use Next.js proxy route instead of direct backend call (avoids CORS/CSP issues)

interface Trader {
  name: string;
  address: string;
  avatar: string;
  pnl: number;
  totalVolume: string;
  currentBalance: number;
  totalPositions: number;
  badge: string;
  rank: number;
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null || typeof value !== 'number' || isNaN(value)) return "$0";
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatVolume(volume: number | undefined | null): string {
  const numVolume = Number(volume) || 0;
  if (numVolume >= 1000000) {
    return `$${(numVolume / 1000000).toFixed(1)}M`;
  } else if (numVolume >= 1000) {
    return `$${(numVolume / 1000).toFixed(1)}K`;
  }
  return `$${numVolume.toFixed(0)}`;
}

export default function TopTradersSection() {
  const t = useT();
  const [traders, setTraders] = useState<Trader[]>([]);

  useEffect(() => {
    async function fetchTraders() {
      try {
        const response = await fetch(`/api/traders/leaderboard?sortBy=volume&limit=3`);
        if (!response.ok) return;
        const data = await response.json();
        const topUsers = data.traders || [];

        setTraders(topUsers.slice(0, 3).map((user: any, index: number) => ({
          name: user.username || `Trader${user.address?.slice(-4) || index}`,
          address: user.address,
          avatar: user.avatar || STATIC_ASSETS.defaultAvatar,
          pnl: Number(user.totalPnL) || 0,
          totalVolume: formatVolume(Number(user.totalVolume)),
          currentBalance: Number(user.currentBalance) || 0,
          totalPositions: Number(user.marketsCount) || 0,
          badge: index === 0 ? t("topTraders.badge.topVolume") : index === 1 ? t("topTraders.badge.risingStar") : t("topTraders.badge.consistent"),
          rank: index + 1,
        })));
      } catch (error) {
        // silently fail
      }
    }
    fetchTraders();
  }, []);

  return (
    <section className="relative py-10 sm:py-16 lg:py-24 bg-gray-50 dark:bg-[#0f0f12] overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-16">
          <p className="text-sm font-monument font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            {t("topTraders.eyebrow")}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-monument font-bold text-gray-900 dark:text-white mb-4">
            {t("topTraders.headline.before")}{" "}
            <span className="text-accent-primary">
              {t("topTraders.headline.accent")}
            </span>
          </h2>
          <p className="text-lg font-satoshi text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            {t("topTraders.subtitle")}
          </p>
        </div>

        {/* Traders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {traders.map((trader, index) => {
            const profileUrl = trader.name.startsWith('Trader')
              ? `/traders/${trader.address}`
              : `/traders/@${trader.name}`;

            return (
              <Link
                key={trader.address || index}
                href={profileUrl}
                prefetch={false}
                className="group block"
              >
                <div className="relative h-full bg-white dark:bg-[#18181D] rounded-[5px] p-6 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#1e1e24] transition-colors overflow-hidden">
                  {/* Rank badge */}
                  {index === 0 && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-20 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-3 py-1 rounded-[5px] text-xs font-bold flex items-center gap-1">
                      #1
                    </div>
                  )}

                  {/* Header with Avatar */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="relative flex-shrink-0">
                      <Image
                        src={trader.avatar}
                        alt={`${trader.name} - Top Polymarket trader profile`}
                        width={56}
                        height={56}
                        sizes="56px"
                        className="w-14 h-14 rounded-[5px] object-cover"
                      />
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-[5px] border-2 border-white dark:border-[#18181D]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-monument font-bold text-gray-900 dark:text-white text-lg mb-1 truncate">{trader.name}</h3>
                      <span className="text-xs font-inter-tight font-bold px-2.5 py-1 bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 rounded-[5px] inline-block">
                        {trader.badge}
                      </span>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="col-span-2 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-4">
                      <div className="text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">{t("topTraders.totalVolume")}</div>
                      <div className="text-2xl font-monument font-extrabold text-gray-900 dark:text-white">
                        {trader.totalVolume}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-3">
                      <div className="text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 mb-0.5 uppercase tracking-wide">{t("topTraders.pnl")}</div>
                      <div className={`text-lg font-monument font-bold ${
                        trader.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'
                      }`}>
                        {trader.pnl >= 0 ? '+' : ''}{formatCurrency(trader.pnl)}
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-3">
                      <div className="text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 mb-0.5 uppercase tracking-wide">{t("topTraders.markets")}</div>
                      <div className="text-lg font-monument font-bold text-gray-900 dark:text-white">
                        {trader.totalPositions}
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-3.5 rounded-[5px] font-inter-tight font-bold text-sm flex items-center justify-center gap-2 group-hover:bg-gray-800 dark:group-hover:bg-gray-100 transition-colors">
                    {t("topTraders.viewProfile")}
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-white dark:bg-[#18181D] rounded-[5px] p-4 sm:p-8 border border-gray-200 dark:border-[#1A1A1A]">
          <h3 className="text-2xl font-monument font-bold text-gray-900 dark:text-white mb-2">
            {t("topTraders.bottom.title")}
          </h3>
          <p className="font-satoshi text-gray-500 dark:text-gray-400 mb-6">
            {t("topTraders.bottom.subtitle")}
          </p>
          <Link
            href="/traders"
            prefetch={false}
            aria-label="View full Polymarket trader leaderboard"
            className="inline-flex items-center gap-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-4 rounded-[5px] font-inter-tight font-bold hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            {t("topTraders.bottom.cta")}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
